import { useCreateBankTransferChallenge } from "@hooks/index";
import {
  Typography,
  Dialog,
  DialogContent,
  Button,
  DialogActions,
  styled,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tracker } from "@utils/openreplay";

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "16px 0px",
  gap: "8px",
  flexDirection: "column-reverse",
  alignItems: "center",
  [theme.breakpoints.up("sm")]: {
    padding: "16px 8px",
    gap: "16px",
    flexDirection: "row",
    alignItems: "end",
  },
}));

const StyledDialog = styled(Dialog)({
  ".MuiDialog-paper": {
    maxWidth: "800px",
    borderRadius: "8px",
    width: "100%",
  },
});

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "100px",
  maxWidth: "290px",
  textTransform: "none",
  "&:hover, &:focus": {
    cursor: "pointer",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "9px 50px",
  },
  gap: "6px",
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const UnableToVerifyResponsesModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: createChallengeRequest } =
    useCreateBankTransferChallenge({
      onError: (error) => {
        setIsLoading(false);
        tracker?.event("BankTransferChallengeRequestedError", {
          source: "UnableToVerifyResponsesModal",
          error: error,
          errorMessage: error.message,
        });
        enqueueSnackbar("Error al generar la transferencia bancaria", {
          variant: "error",
        });
        console.error(
          "Error creating bank transaction challenge request: ",
          error
        );
      },
    });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCreateTransferChallengeRequest = useCallback(async () => {
    tracker?.event("BankTransferChallengeRequested", {
      source: "UnableToVerifyResponsesModal",
    });
    setIsLoading(true);
    await createChallengeRequest();
  }, [createChallengeRequest]);

  return (
    <StyledDialog open={isOpen} onClose={handleClose}>
      <DialogTitle>No fue posible verificar las respuestas</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1">
          Puedes completar la verificación con una{" "}
          <Typography component="span" fontWeight={600}>
            transferencia bancaria a través de Khipu
          </Typography>{" "}
          (monto cercano a $2.500), que será{" "}
          <Typography component="span" fontWeight={600}>
            reembolsado automáticamente
          </Typography>{" "}
          dentro de los próximos 2 días hábiles.
        </Typography>
      </DialogContent>
      <StyledDialogActions>
        <StyledButton
          onClick={() => navigate("/app/solicitudes")}
          variant="text"
          color="primary"
          disabled={isLoading}
        >
          Volver al inicio
        </StyledButton>
        <StyledButton
          variant="contained"
          onClick={handleCreateTransferChallengeRequest}
          disabled={isLoading}
        >
          Realizar transferencia
          {isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};
